import { Body, Bullets, Em, GOLD_DEEP, HAIR, Hero, IVORY, Kicker, More, Ornament, Rows, SLATE, Section, Serif, Title } from "../components/ms/kit";
import InquiryForm from "../components/ms/InquiryForm";
import PageShell from "../components/ms/PageShell";
import { Split } from "./Angels";

const SCALE = [
  ["Scaling a team", "When to hire leaders, how to keep culture as headcount grows, and what to change in how you run the company."],
  ["Scaling operations", "Building the systems and processes that let a company grow without breaking."],
  ["Scaling your fundraise and strategy", "What later-stage investors look for, how to run a process, and how to bring Canadian and US investors to a Prairie company."],
  ["Scaling go-to-market and sales", "Building a repeatable sales motion and choosing where to grow next."],
];

const EXIT = [
  ["IPO", "What it takes to go public, and whether it is the right path."],
  ["Pure acquisition", "How acquirers value a company and what makes a business attractive to them."],
  ["M&A", "How deals are structured and how to prepare for one before you need to."],
];

const WHY = [
  ["Operators, not theory", "Every session is led by someone who has scaled, raised or exited a company, and members hear what they would do differently.", "01"],
  ["Built around the real questions", "The topics come from what scaleup founders are asking, not from a fixed curriculum.", "02"],
  ["Part of a larger ecosystem", "MavenCircle sits alongside the Angel Investment Club and Groundwork, so members can meet angels, emerging fund managers and early-stage founders who may become customers, hires and investors.", "03"],
  ["Peers who stay", "The relationships built here continue after the sessions end.", "04"],
];

const FIT = [
  "Your company has found product-market fit and you are now focused on growth.",
  "You are building a team, preparing for a larger raise, or thinking about what an eventual exit could look like.",
  "You want to learn from, and be known by, other Prairie founders at the same stage.",
  "You would rather ask a peer who has been through it than read about it.",
];

const FIELDS = [
  { key: "name", label: "Your name and title" },
  { key: "email", label: "Email", type: "email" },
  { key: "company", label: "Company name and website" },
  { key: "city", label: "City and province" },
  { key: "what", label: "What your company does, in a sentence or two", type: "textarea", wide: true },
  { key: "stage", label: "Your stage", options: ["Series A", "Series B or beyond", "Profitable and growing", "Other"], wide: true },
  { key: "team", label: "Team size" },
  { key: "help", label: "What you would most like help with", options: ["Scaling a team", "Scaling operations", "Raising a larger round", "Go-to-market and sales", "Going global", "Planning an exit"], multi: true, wide: true },
  { key: "share", label: "Anything you would like to share", type: "textarea", optional: true, wide: true },
];

const Week = ({ title, intro, items, testId }) => (
  <div className="mt-20" data-testid={testId}>
    <h3 className="text-center" style={{ ...Serif, color: IVORY, fontSize: "clamp(1.6rem, 2.6vw, 2.1rem)" }}>{title}</h3>
    <Body className="mt-4 text-center max-w-2xl mx-auto">{intro}</Body>
    <Rows items={items} labelWidth="md:grid-cols-[300px_1fr]" />
  </div>
);

const Note = ({ title, children }) => (
  <div className="py-8" style={{ borderBottom: HAIR }}>
    <p className="text-[11px] tracking-[0.3em] uppercase" style={{ color: GOLD_DEEP }}>{title}</p>
    <Body className="mt-3">{children}</Body>
  </div>
);

export default function MavenCircle() {
  return (
    <PageShell testId="mavencircle-page">
      <Hero kicker="MavenCircle by Maven Skies" testId="mc"
        body="Building a company past the early stage is a different job from starting one. MavenCircle brings together founders who are raising larger rounds, growing teams and planning exits, and puts them in the room with experienced entrepreneurs who have done it."
        extra={<><Ornament /><p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: GOLD_DEEP }}>Building people · Building capital · Building the future</p></>}>
        A peer network for Prairie <Em>scaleup founders.</Em>
      </Hero>
      <Split kicker="I · The Problem" title={<>Scaling is lonely, and most programs <Em>stop too early.</Em></>} testId="mc-problem">
        <Body>Most startup programming is built for the first stage of a company: finding a problem, building a product and raising a first round. Once a company has traction, the questions get harder and fewer people can answer them. When should we hire a leadership team? What do Series B investors expect? How do we sell into new markets? What would an acquirer want to see?</Body>
        <Body tone={IVORY}>Founders at this stage often work through these questions alone, because the people who have answered them before are in other cities. Prairie scaleups should not have to work through them without help.</Body>
      </Split>
      <Section testId="mc-model" width="max-w-5xl">
        <div className="text-center max-w-3xl mx-auto">
          <Kicker>II · The Model</Kicker>
          <Title className="mt-6">Learn from founders who have done it, <Em>in the order a company lives it.</Em></Title>
          <Body className="mt-8">MavenCircle follows the real arc of a growing company: scale, raise, then exit. Each stage has its own theme week, led by entrepreneurs and operators who have built and sold companies, not by consultants describing the theory.</Body>
        </div>
        <Week title="Scale Week" intro="Four sessions on the operating questions that decide whether a company grows or stalls." items={SCALE} testId="mc-scale-week" />
        <Week title="Exit Week" intro="Three sessions on how companies are bought, sold and taken public, and what that means for founders and early investors." items={EXIT} testId="mc-exit-week" />
        <div className="mt-20 max-w-3xl mx-auto" style={{ borderTop: HAIR }}>
          <Note title="Panels for the whole community">Alongside the theme weeks, MavenCircle members are welcome at peer-to-peer panels on raising Series B and beyond, scaling teams and operations, exits, and going global.</Note>
          <Note title="Peers, not just speakers">Every session is a conversation. Members ask their own questions, compare notes with other scaleup founders, and leave with a small group of peers who understand their stage.</Note>
        </div>
      </Section>
      <Section testId="mc-why" width="max-w-5xl">
        <div className="text-center"><Kicker>III · Why It Is Different</Kicker><Title className="mt-6">Built by people who have <Em>done it.</Em></Title></div>
        <Rows items={WHY} testId="mc-why-list" labelWidth="md:grid-cols-[320px_1fr]" />
      </Section>
      <Section testId="mc-fit" width="max-w-5xl">
        <div className="text-center"><Kicker>IV · Is MavenCircle Right for You?</Kicker><Title className="mt-6">MavenCircle is for you <Em>if:</Em></Title></div>
        <Bullets items={FIT} testId="mc-fit-list" />
        <p className="mt-10 max-w-3xl mx-auto text-center text-[15px] leading-[1.75]" style={{ color: SLATE }}>
          If you are earlier in your journey, our Founder Investment Readiness Program is a better place to start. We would be glad to point you there. <More to="/program#apply" testId="mc-link-founder-program">Founder Investment Readiness Program</More>
        </p>
      </Section>
      <Section id="join" testId="mc-join" className="py-32 md:py-44" width="max-w-4xl">
        <div className="text-center">
          <Kicker>V · Join MavenCircle</Kicker>
          <Title className="mt-8">Scaling is easier with people who have <Em>already done it.</Em></Title>
          <Body className="mt-8 max-w-2xl mx-auto">Membership is by invitation, and we read every request personally. Tell us about your company and what you are working on, and we will be in touch about the next steps.</Body>
        </div>
        <InquiryForm form="mavencircle" fields={FIELDS} submitLabel="Request Invitation" testId="mc-form"
          thanks="Your request has reached us. We read every request personally and will be in touch about the next steps." />
      </Section>
    </PageShell>
  );
}
